/**
 * spin-media.js — hero animation that reads as a still product photo.
 *
 * The clip is authored as a seamless cycle: its final frame is the same pose as its
 * first. It plays once and stops, so it comes to rest on the frame the visitor already
 * saw as the "product photo". Before and after, the hero looks like an ordinary image.
 *
 * Performance contract (the reason this file is shaped the way it is):
 *   - The <video> is NEVER in the server-rendered HTML. It is created here, so the
 *     browser cannot fetch video bytes in competition with the hero image.
 *   - The existing gallery <img> is never modified. It keeps its srcset/sizes/WebP and
 *     remains the LCP element. This element only overlays a video on top of it.
 *   - No video bytes at all on reduced-motion, Save-Data, 2g, or if the hero is never
 *     scrolled into view.
 *   - Every failure path degrades to "a correct static product photo".
 *
 * Reliability contract. Both reported symptoms — "nothing happens on my phone" and
 * "hovering did nothing for two or three seconds" — turned out to be trigger problems,
 * not media problems, so these are now rules of the file:
 *   - Every device can start it. Pointer, touch, keyboard focus and scroll-into-view are
 *     all wired, and none of them is a dead end.
 *   - A gesture is never ignored. If a visitor asks for the animation, it starts now.
 *   - No state is terminal-but-silent. If playback is refused, the next tap plays it.
 *
 * Debug: append ?spin=debug. Force past the bail-outs: ?spin=force. Disable: ?spin=off.
 */

(() => {
  const PARAM = new URLSearchParams(window.location.search).get('spin');
  const DEBUG = PARAM === 'debug';
  const FORCE = PARAM === 'force';
  const DISABLED = PARAM === 'off';

  /** Idle deadline before we give up waiting for a quiet moment and just proceed. */
  const IDLE_TIMEOUT_MS = 2000;
  /** Fraction of the hero that must be on screen before we fetch the clip. */
  const VISIBILITY_THRESHOLD = 0.5;
  /** Ceiling on waiting for the hero image, so one stalled image cannot strand the clip. */
  const POSTER_WAIT_MS = 3000;
  /** How long to wait for a playable video before assuming the browser refuses to buffer. */
  const BUFFER_TIMEOUT_MS = 6000;

  /**
   * Every way a visitor can ask for the animation.
   *
   * `pointerenter` is the intent signal, but it fires ONLY on a transition from outside
   * the target to inside it. A cursor already resting on the hero when this element
   * upgrades never produces one, so the hero looked dead until the visitor moved the
   * mouse away and back — one of the two reported symptoms. `pointermove` covers exactly
   * that case, `focusin` covers keyboard, and `pointerdown` covers touch.
   */
  const GESTURES = ['pointerenter', 'pointermove', 'focusin', 'pointerdown'];

  /** Controls that own their own click. A gesture on one of these is not a spin request. */
  const FOREIGN_CONTROLS = '.ingredient-reveal__panel, .ingredient-reveal__button';

  /**
   * Only one element may own a given clip at a time. The product page renders the
   * gallery twice (mobile vs desktop, mutually exclusive by breakpoint) and we must
   * never fetch the same video into both.
   */
  const claims = new Map();

  const log = (...args) => {
    if (DEBUG) console.log('[spin-media]', ...args);
  };

  /** Compare media URLs ignoring CDN transform params, so ?width= changes don't count. */
  const baseUrl = (url) => (url || '').split('?')[0];

  const canHover = () => window.matchMedia('(hover: hover)').matches;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onSlowConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return 'save-data';
    if (/(^|-)2g$/.test(conn.effectiveType || '')) return conn.effectiveType;
    return false;
  }

  /** Did this gesture land on something that owns its own click? */
  function onForeignControl(event) {
    return !!event?.target?.closest?.(FOREIGN_CONTROLS);
  }

  /**
   * Resolve once the hero image has finished loading.
   *
   * This replaced a wait on the window `load` event, which was the single biggest cause
   * of the hero looking broken: `load` waits for every image, script and iframe on the
   * page — measured at 4775 ms on the QA page — so the animation could not even begin
   * for nearly five seconds. The only thing the clip must not compete with is the image
   * it overlays, which makes that image completing both the correct signal and a far
   * tighter one. The LCP guarantee is unchanged, and arguably stronger for being tied to
   * the element actually at risk rather than to the whole page.
   */
  function whenHeroImageSettles(img) {
    if (!img || img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      window.setTimeout(finish, POSTER_WAIT_MS);
    });
  }

  /** Resolve on the next genuinely idle moment, with a deadline. */
  function idleTick() {
    return new Promise((resolve) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(resolve, { timeout: IDLE_TIMEOUT_MS });
      } else {
        window.setTimeout(resolve, 200);
      }
    });
  }

  class SpinMedia extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.image = null;
      this.observer = null;
      this.srcWatcher = null;
      this.started = false;
      this.torndown = false;
      this.posterSignature = '';
      this._gestureFire = null;
      this._gestureTarget = null;
      this._gestureSeen = false;
      this._replay = null;
      this._replayTarget = null;
      this._tapPlay = null;
      this._tapPlayTarget = null;
      this._revealTimer = 0;
      this._bufferTimer = 0;
    }

    get src() {
      return this.dataset.spinSrc || '';
    }

    /**
     * Key used to enforce "one owner per clip". Defaults to the clip URL, which is what
     * makes the duplicated product gallery safe. The preview page overrides it so it can
     * deliberately show the same clip in two panes at once.
     */
    get claimKey() {
      return this.dataset.spinKey || this.src;
    }

    /**
     * When to load and play.
     *
     * `view`  (default) — play once when scrolled into view. Right for a product hero,
     *          where the animation IS the hero treatment.
     * `hover` — do nothing until the pointer enters. Right for collection cards: four
     *          cards animating themselves at once is visual noise, and it also means a
     *          grid costs zero video bytes until someone actually hovers a card.
     *
     * A `hover` trigger is meaningless on a device with no pointer. The old code simply
     * gave up there, which is why every animated card was permanently static on a phone
     * no matter what the visitor touched. Touch falls back to `view`: play once when
     * scrolled into view, the closest honest equivalent of a hover, and one that still
     * costs zero bytes for any card never scrolled to.
     */
    get trigger() {
      const want = this.dataset.spinTrigger === 'hover' ? 'hover' : 'view';
      if (want === 'hover' && !canHover()) return 'view';
      return want;
    }

    /** Playback rate, used to retime without re-encoding. */
    get rate() {
      const rate = parseFloat(this.dataset.spinRate || '1');
      return Number.isFinite(rate) && rate > 0 ? rate : 1;
    }

    /**
     * The gallery image this element overlays. Scoped to the surrounding slide so a
     * page with several galleries can never cross-wire them.
     */
    findImage() {
      const scope = this.closest('.product__media-item') || this.parentElement;
      if (!scope) return null;
      return scope.querySelector('.product-media-container img, .media img, img');
    }

    /**
     * `.media` is `position: relative; overflow: hidden` in base.css, and its
     * `.media > *` rule already absolutely positions children to fill it — so a video
     * appended here inherits correct geometry without extra CSS.
     */
    findMediaBox() {
      return this.image?.closest('.media') || null;
    }

    /**
     * The element to bind pointer interaction to. NOT the media box.
     *
     * A product card renders a full-card link overlay (`.card__heading a`, whose
     * absolutely-positioned `::after` covers the whole card) ABOVE the media. Hit-testing
     * the centre of a card image returns `a.full-unstyled-link`, and `.media` is not in
     * that path at all — so `pointerenter` bound to `.media` never fires on a card. The
     * theme's own second-image swap kept working throughout because it is CSS keyed on
     * `.card-wrapper:hover`, and `.card-wrapper` IS an ancestor of the overlay.
     *
     * Binding to the card root fixes it and matches the theme's own hover semantics:
     * hovering anywhere on the card triggers it, exactly as the image swap does.
     * On a product page there is no overlay, so this resolves to the gallery slide and
     * behaves as before.
     */
    findHoverTarget() {
      return this.closest('.card-wrapper, .card, .product__media-item') || this.findMediaBox();
    }

    connectedCallback() {
      if (DISABLED) return log('disabled via ?spin=off');
      if (!this.src) return log('no data-spin-src, nothing to do');

      // A re-connect (a section re-render, a slider re-parenting the slide) runs this
      // again on the SAME instance. Without resetting these, every guarded path below
      // early-returns on the stale flags and the element is permanently, silently dead.
      this.torndown = false;
      this.started = false;
      this._gestureSeen = false;

      if (!FORCE) {
        if (prefersReducedMotion()) return log('skipped: prefers-reduced-motion');
        const slow = onSlowConnection();
        if (slow) return log(`skipped: ${slow}`);
      }

      if (!('IntersectionObserver' in window)) return log('skipped: no IntersectionObserver');

      this.image = this.findImage();
      if (!this.image) return log('skipped: no sibling image found to overlay');
      this.posterSignature = this.imageSignature();

      // Mark the media box so CSS can target an animated hero without needing :has().
      // spin-media.css uses it to cancel the card's hover zoom — see the note there.
      //
      // Deliberately NOT removed on teardown. On cards the same class is also emitted
      // server-side by card-product.liquid, because it is paired with SUPPRESSING THE
      // SECOND IMAGE, not with a video existing. Removing it would restore the theme's
      // zoom on a card that has no second image left to swap to, which is the regression
      // it was added to fix. Product heroes never zoom, so leaving it costs nothing.
      this.findMediaBox()?.classList.add('spin-media-host');

      this.watchForVariantChange();

      // Armed for BOTH triggers, always. For `hover` this IS the trigger. For `view` it is
      // a fast path: a gesture means the visitor is looking at the hero right now, so it
      // starts immediately instead of waiting out the idle window.
      this.armGestures();

      if (this.trigger === 'hover') {
        this.setAttribute('data-spin-state', 'idle');
        return;
      }

      this.setAttribute('data-spin-state', 'waiting');
      // Registering an observer costs no bytes, so it happens now rather than after the
      // page has settled. Only the FETCH is deferred — see whenSafeToFetch.
      this.observe();
    }

    disconnectedCallback() {
      this.teardown('disconnected');
    }

    /**
     * Bind every gesture that means "show me the animation".
     *
     * Idempotent and re-bindable on purpose, because `rearm()` calls it again when a
     * variant switch returns to the pose the clip was authored against.
     */
    armGestures() {
      if (this._gestureFire) return;
      const target = this.findHoverTarget();
      if (!target) return log('skipped: nothing to arm for interaction');

      const fire = (event) => {
        // A tap on the ingredient control is a request for ingredients, not for the
        // animation. Loading a clip there spends bytes on the one gesture that hides it.
        if (onForeignControl(event)) return;
        this.onGesture();
      };

      this._gestureFire = fire;
      this._gestureTarget = target;
      GESTURES.forEach((type) => target.addEventListener(type, fire, { passive: true }));

      // If the pointer is ALREADY inside at bind time there is no future event to wait
      // for, so check the current state directly. This is the whole fix for "hovering did
      // nothing until I moved the mouse away and came back".
      if (target.matches?.(':hover')) {
        log('pointer already inside at arm time — starting now');
        fire();
      }

      log('armed on', target.className || target.tagName, '— nothing loaded yet');
    }

    disarmGestures() {
      if (!this._gestureFire || !this._gestureTarget) return;
      GESTURES.forEach((type) => this._gestureTarget.removeEventListener(type, this._gestureFire));
      this._gestureFire = null;
      this._gestureTarget = null;
    }

    /**
     * A visitor asked for the animation. Start it now, skipping any remaining wait.
     *
     * No claim contest is needed for an already-held key: claim() returns true when this
     * element is the holder, and a gesture can only come from a hero that is genuinely on
     * screen and interactive, which makes it the right owner.
     */
    onGesture() {
      this._gestureSeen = true;
      if (this.video || this.torndown) return;
      // The breakpoint-hidden gallery copy must never fetch.
      if (!this.isRendered()) return log('gesture on a hero that is not rendered — ignoring');
      if (!this.claim()) return log('another gallery already owns this clip');

      this.observer?.disconnect();
      this.observer = null;
      this.disarmGestures();
      this.load();
    }

    /**
     * Observe the hero image rather than this element. This element is `display: none`
     * (it is a pure coordinator and must not affect layout), and a `display: none`
     * element never reports as intersecting.
     *
     * Watching the image is also what makes the duplicated gallery safe for free: the
     * breakpoint-hidden copy has no visible image, so it never loads the clip.
     */
    observe() {
      const target = this.findMediaBox() || this.image;
      if (!target) return log('skipped: nothing observable to watch');

      this.observer = new IntersectionObserver(
        (entries) => {
          // A notification can already be queued when teardown runs (a variant switch, or
          // the section being re-rendered). Without this guard the callback would
          // re-register a dead element in `claims` — permanently blocking the other
          // gallery copy — and then throw on `this.observer.disconnect()`, which teardown
          // has already nulled.
          if (this.torndown || !this.observer) return;
          if (!entries.some((entry) => entry.isIntersecting)) return;
          if (!this.claim()) return log('another gallery already owns this clip');
          this.observer.disconnect();
          this.observer = null;
          this.whenSafeToFetch().then(() => {
            if (this.torndown || !this.isConnected) return;
            this.load();
          });
        },
        { threshold: this.visibilityThreshold(target) },
      );
      this.observer.observe(target);
      log('observing the hero');
    }

    /**
     * How much of the hero must be visible before fetching.
     *
     * Half of it is the right signal for a normal box, but a hero TALLER than the viewport
     * can never reach 50% visibility — so on a phone in portrait a tall hero would
     * silently never load at all. Those get a low threshold instead.
     */
    visibilityThreshold(target) {
      const height = target.getBoundingClientRect().height;
      return height > window.innerHeight * 0.8 ? 0.1 : VISIBILITY_THRESHOLD;
    }

    /**
     * Hold the fetch until it cannot compete with the image it overlays, then one idle
     * tick. A gesture already seen means the visitor is waiting, so skip both.
     */
    whenSafeToFetch() {
      if (this._gestureSeen) return Promise.resolve();
      return whenHeroImageSettles(this.image).then(idleTick);
    }

    /** Is this instance's hero actually rendered? False for the hidden gallery copy. */
    isRendered() {
      const box = this.findMediaBox();
      return !!box && box.offsetParent !== null;
    }

    /**
     * Take ownership of this clip. Steals from a holder whose hero is no longer
     * rendered, which is what happens when a resize flips between the mobile and
     * desktop gallery.
     */
    claim() {
      const holder = claims.get(this.claimKey);
      if (holder && holder !== this && holder.isConnected && holder.isRendered()) {
        return false;
      }
      claims.set(this.claimKey, this);
      return true;
    }

    release() {
      if (claims.get(this.claimKey) === this) claims.delete(this.claimKey);
    }

    /**
     * Identity of the image file(s) the hero is currently pointing at.
     *
     * Built from the `src` and `srcset` ATTRIBUTES, deliberately not from `currentSrc`:
     * `currentSrc` is populated asynchronously once the browser has selected and begun
     * loading a candidate, so at mutation time it still holds the previous value and a
     * variant switch would be missed.
     *
     * CDN transform params are stripped, so responsive width changes on the same file
     * collapse to the same signature and do not count as a change.
     */
    imageSignature() {
      const img = this.image;
      if (!img) return '';
      const urls = [baseUrl(img.getAttribute('src') || '')];
      (img.getAttribute('srcset') || '').split(',').forEach((candidate) => {
        const url = baseUrl(candidate.trim().split(/\s+/)[0]);
        if (url && !urls.includes(url)) urls.push(url);
      });
      return urls.filter(Boolean).join('|');
    }

    /**
     * A variant switch can swap the hero to a different shot. The clip only matches the
     * pose it was authored against, so if the image changes underneath us the animation
     * must go rather than play an animation of the wrong product.
     *
     * It comes BACK if the visitor switches back. The old code tore the element down
     * permanently on the first change, so browsing variants and returning left a dead
     * hero for the rest of the session with nothing to indicate why.
     */
    watchForVariantChange() {
      if (!('MutationObserver' in window)) return;
      if (this.srcWatcher) return;
      this.srcWatcher = new MutationObserver(() => {
        const now = this.imageSignature();
        if (!now || !this.posterSignature) return;

        if (now !== this.posterSignature) {
          if (this.video || this._gestureFire || this.observer) {
            log('hero image changed (variant switch) — standing down');
            this.suspend();
          }
          return;
        }

        if (!this.video && !this._gestureFire && !this.observer) {
          log('hero image is back to the clip pose — re-arming');
          this.rearm();
        }
      });
      this.srcWatcher.observe(this.image, { attributes: true, attributeFilter: ['src', 'srcset'] });
    }

    load() {
      // Idempotent on purpose. `this.video` is the guard because several paths can
      // legitimately race: a gesture and the observer's deferred fetch, or armGestures
      // binding both pointerenter and pointermove. Each would otherwise append its own
      // <video>, and the loser would sit orphaned in the DOM, still downloading and
      // invisible to teardown.
      if (this.video) return log('already loaded, ignoring duplicate trigger');
      const box = this.findMediaBox();
      if (!box) return log('skipped: no .media container to mount into');

      const video = document.createElement('video');
      video.className = 'spin-media__video';
      // `?spin=force` bypasses the reduced-motion bail-out in JS, but the CSS still hid
      // the video — so the clip was fetched and played and never seen, making the
      // debugging switch silently useless. This opts the video out of that CSS bail too.
      if (FORCE) video.classList.add('spin-media__video--forced');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = false;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      // The still image already conveys the product to assistive tech, and the clip
      // adds no information of its own, so it is presentational.
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      // Poster matches the underlying image, so an early paint cannot flash.
      video.poster = this.image.currentSrc || this.image.src;

      // Honour the section's media_fit setting without duplicating that logic.
      const imgStyle = window.getComputedStyle(this.image);
      video.style.objectFit = imgStyle.objectFit || 'cover';
      video.style.objectPosition = imgStyle.objectPosition || 'center';

      video.addEventListener('canplay', () => this.onReady(), { once: true });
      // `canplay` is the ideal signal but it never arrives on a browser that refuses to
      // buffer media (iOS Low Power Mode, Data Saver). `loadeddata` is the weaker but more
      // widely delivered one, and onReady is idempotent, so listening for both removes a
      // whole class of "stuck at loading forever".
      video.addEventListener('loadeddata', () => this.onReady(), { once: true });
      // NOT `once`: replays fire `ended` again, and without this the state attribute would
      // stay "playing" forever after the first cycle. onEnded is idempotent.
      video.addEventListener('ended', () => this.onEnded());
      video.addEventListener('error', () => {
        log('video failed to load — keeping the still image');
        this.teardown('error');
      }, { once: true });

      video.src = this.src;
      box.appendChild(video);
      this.video = video;
      this.setAttribute('data-spin-state', 'loading');
      log('loading', this.src);

      // Nothing above is guaranteed to fire. If no readiness event arrives, hand the
      // visitor a way to start it rather than sitting at `loading` for the whole session.
      window.clearTimeout(this._bufferTimer);
      this._bufferTimer = window.setTimeout(() => {
        if (this.torndown || this.started || !this.video) return;
        log('no readiness event within the timeout — arming tap to play');
        this.setAttribute('data-spin-state', 'blocked');
        this.armTapToPlay();
      }, BUFFER_TIMEOUT_MS);

      if (DEBUG) this.reportWeight();
    }

    /** Idempotent: `canplay` and `loadeddata` both route here, and either may come first. */
    onReady() {
      if (this.torndown || !this.video || this.started) return;
      this.started = true;
      window.clearTimeout(this._bufferTimer);
      this.video.playbackRate = this.rate;
      this.play();
      this.reveal();
      this.enableReplay();
    }

    /**
     * Show the video only once it has actually presented a frame.
     *
     * `canplay` means "enough data buffered to start", NOT "a frame is on screen". Making
     * the element visible then can expose an undecoded video box for a beat, which reads
     * as a flicker just before the animation starts. `requestVideoFrameCallback` fires
     * after a frame has been composited, so the swap happens on a frame that is ready.
     *
     * The event and timer fallbacks exist because BOTH of those paths depend on the
     * rendering lifecycle, which a browser freezes in a backgrounded tab, and on a frame
     * actually being composited. Events and timers keep running, so together they rule
     * out the worst outcome of all: a clip fetched, decoded and played while completely
     * invisible. They are safe to reveal on because the video's poster IS the underlying
     * still, so an early reveal shows a frame identical to what was already on screen.
     */
    reveal() {
      const video = this.video;
      if (!video) return;
      if (video.classList.contains('spin-media__video--ready')) return;

      const show = () => {
        if (this.torndown || !this.video) return;
        this.video.classList.add('spin-media__video--ready');
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(show);
      } else {
        // Two frames, not one: the first only guarantees we are past the current paint.
        requestAnimationFrame(() => requestAnimationFrame(show));
      }

      video.addEventListener('playing', show, { once: true });
      video.addEventListener('timeupdate', show, { once: true });

      window.clearTimeout(this._revealTimer);
      this._revealTimer = window.setTimeout(() => {
        if (this.video && this.video.currentTime > 0) show();
      }, 400);
    }

    play() {
      const video = this.video;
      if (!video) return;
      const attempt = video.play();
      if (!attempt?.then) {
        this.setAttribute('data-spin-state', 'playing');
        return;
      }
      attempt.then(() => {
        if (this.torndown) return;
        this.setAttribute('data-spin-state', 'playing');
        this.reveal();
      }).catch(() => {
        // iOS Low Power Mode and Safari's "Auto-Play: Never" refuse even a muted inline
        // video. A distinct state, not `idle`, so this is distinguishable from "nothing
        // loaded yet" in QA and in the debug readout.
        log('playback refused — arming tap to play');
        this.setAttribute('data-spin-state', 'blocked');
        this.armTapToPlay();
      });
    }

    /**
     * The reason a refused autoplay is no longer a dead end.
     *
     * Every policy that refuses programmatic playback still accepts playback started
     * inside a real gesture, so the next tap on the hero plays it. Deliberately no
     * visible control: there is no new copy to translate, and the still image is already
     * the correct resting state, so a visitor who never taps sees exactly what they
     * should see.
     */
    armTapToPlay() {
      if (this._tapPlay) return;
      const target = this.findHoverTarget() || this.findMediaBox();
      if (!target) return;

      const onTap = (event) => {
        if (onForeignControl(event)) return;
        const video = this.video;
        if (!video) return;
        // Must be called synchronously inside the gesture or the policy refuses again.
        video.play().then(() => {
          if (this.torndown) return;
          this.started = true;
          this.setAttribute('data-spin-state', 'playing');
          this.reveal();
          this.enableReplay();
          this.disarmTapToPlay();
        }).catch(() => log('tap to play was refused too'));
      };

      this._tapPlay = onTap;
      this._tapPlayTarget = target;
      target.addEventListener('click', onTap);
      log('tap to play armed');
    }

    disarmTapToPlay() {
      if (!this._tapPlay || !this._tapPlayTarget) return;
      this._tapPlayTarget.removeEventListener('click', this._tapPlay);
      this._tapPlay = null;
      this._tapPlayTarget = null;
    }

    onEnded() {
      // Deliberately do nothing: the final frame equals the poster, so holding it is
      // exactly the "static photo" resting state. Swapping anything here would flash.
      this.setAttribute('data-spin-state', 'rested');
      log('rested on the opening pose');
    }

    /**
     * Re-hover replays the clip.
     *
     * Bound to `pointerenter` and NOT `pointermove`, deliberately: a move listener would
     * restart the clip continuously while the cursor crossed the hero. That is the exact
     * opposite of the arming case, where a move listener is what is needed.
     */
    enableReplay() {
      if (this._replay) return;
      const target = this.findHoverTarget();
      if (!target) return;

      const replay = (event) => {
        const video = this.video;
        if (!video) return;

        if (event?.type === 'click') {
          // A click on a hero that ALSO carries the ingredient reveal belongs to that
          // control. ingredient-reveal binds its toggle to the `.media` box, a DESCENDANT
          // of this target, so without this guard one click both opened the panel and
          // restarted the clip — which then played out unseen at 6% opacity behind the
          // scrim. That is why the animation appeared to need two clicks on the
          // ingredient control before it would start.
          if (this.findMediaBox()?.classList.contains('has-ingredient-reveal')) return;
          if (onForeignControl(event)) return;
        }

        // Ignore the trigger while a cycle is already running.
        if (!video.paused && !video.ended) return;
        video.currentTime = 0;
        this.play();
      };

      target.addEventListener('pointerenter', replay);
      target.addEventListener('click', replay);
      this._replay = replay;
      this._replayTarget = target;
    }

    disarmReplay() {
      if (!this._replay || !this._replayTarget) return;
      this._replayTarget.removeEventListener('pointerenter', this._replay);
      this._replayTarget.removeEventListener('click', this._replay);
      this._replay = null;
      this._replayTarget = null;
    }

    /** Debug-only: read the real transferred size from the Resource Timing entry. */
    reportWeight() {
      if (!('PerformanceObserver' in window)) return;
      const filename = baseUrl(this.src).split('/').pop();
      // `includes('')` is true for every resource, so an empty filename would report some
      // unrelated request as the clip.
      if (!filename) return;
      const obs = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!entry.name.includes(filename)) return;
          const kb = (entry.transferSize / 1024).toFixed(0);
          const cached = entry.transferSize === 0 ? ' (from cache)' : '';
          log(`clip transferred: ${kb} KB${cached} in ${entry.duration.toFixed(0)}ms`);
          this.dispatchEvent(new CustomEvent('spin-media:loaded', {
            bubbles: true,
            detail: { bytes: entry.transferSize, ms: entry.duration },
          }));
          obs.disconnect();
        });
      });
      try {
        obs.observe({ type: 'resource', buffered: true });
      } catch {
        // Resource timing unavailable; debug readout is simply absent.
      }
    }

    destroyVideo() {
      if (!this.video) return;
      this.video.pause();
      // Detach the source so the browser can release the buffered data.
      this.video.removeAttribute('src');
      this.video.load();
      this.video.remove();
      this.video = null;
    }

    /**
     * Stand down without dying: drop the video and every trigger, but keep watching the
     * hero image so a switch back can bring this round again. `teardown` is this plus
     * releasing that watch.
     */
    suspend() {
      this.observer?.disconnect();
      this.observer = null;
      window.clearTimeout(this._revealTimer);
      window.clearTimeout(this._bufferTimer);
      this.disarmGestures();
      this.disarmReplay();
      this.disarmTapToPlay();
      this.destroyVideo();
      this.started = false;
      this._gestureSeen = false;
      this.release();
      this.setAttribute('data-spin-state', 'off');
    }

    rearm() {
      if (this.torndown || !this.isConnected) return;
      this.armGestures();
      if (this.trigger === 'hover') {
        this.setAttribute('data-spin-state', 'idle');
        return;
      }
      this.setAttribute('data-spin-state', 'waiting');
      this.observe();
    }

    teardown(reason) {
      if (this.torndown) return;
      this.torndown = true;
      this.srcWatcher?.disconnect();
      this.srcWatcher = null;
      this.suspend();
      log('torn down:', reason);
    }
  }

  if (!customElements.get('spin-media')) {
    customElements.define('spin-media', SpinMedia);
  }
})();
